import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from 'antd';
import { Button, SvgIcon } from '@/base-components';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import {
    clearSession,
    fetchHistoryTopics,
    abortStreamAsync,
    createNewSession
} from '@/store/slices/chatSlice';
import { changeTheme } from '@/store/slices/theme';
import { trackEvent } from '@/service/tracking';
import { getStoredCountry } from '@/service/api';
import routePath from '@/routes/route-path';
import { useDevice, useThemeToken } from '@/hooks';
import './Sidebar.less';

interface SidebarPropsType {
    width?: number;
}

const { Sider } = Layout;

const Sidebar: React.FC<SidebarPropsType> = (props) => {
    const {
        width = 80
    } = props;

    const [ settingOpen, setSettingOpen ] = useState(false);

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const curTheme = useSelector((state: RootState) => state.theme);
    const { currentSessionId, isSessionLoading } = useSelector((state: RootState) => state.chat);
    const isHistoryDisabled = !currentSessionId || isSessionLoading;
    const historyHoverTip = isHistoryDisabled ? 'Initializing session...' : 'Historical Issue';

    const { colorSurfaceCanvas } = useThemeToken();
    const layoutMode = useDevice();

    const settingList = useMemo(() => {
        const themeList = [
            {
                iconName: 'sun',
                name: 'Light',
                theme: 'light'
            },
            {
                iconName: 'moon',
                name: 'Dark',
                theme: 'dark'
            },
            {
                iconName: 'gradient',
                name: 'Gradient',
                theme: 'light-blue_'
            }
        ];

        return (
            <div className="setting-menu-inner" role="menu" aria-label="Theme selection">
                <>
                    {
                        themeList.map(themeItem => (
                            <SvgIcon
                                className={curTheme === themeItem.theme ? 'menu-seleted' : ''}
                                key={themeItem.name}
                                marginRight={8}
                                name={themeItem.iconName as any}
                                onClick={() => handleThemeToggle(themeItem.theme)}
                                {...({ tabIndex: 0, 'aria-label': themeItem.name } as any)}
                            >
                                {themeItem.name}
                            </SvgIcon>
                        ))
                    }
                </>
                {/* <Divider
                    style={{
                        margin: 0,
                        background: colorPrimaryBorder
                    }}
                />
                <SvgIcon name="accessibility">Accessibility</SvgIcon> */}
            </div>
        );
    }, [ curTheme, settingOpen ]);

    const handleNewChat = () => {
        // 新建/切换对话时，彻底打断之前的流（网络和前端状态）
        dispatch(abortStreamAsync());
        dispatch(clearSession());

        // 直接用缓存的国家创建新会话，不再跳首页弹国家选择
        const country = getStoredCountry();
        if (country) {
            dispatch(createNewSession({ country }));
        }

        navigate(routePath.home, { replace: true, state: { suppressAgreement: true, fromNewChat: true } });
    };

    const handleHistoryClick = () => {
        if (isHistoryDisabled) {
            return;
        }
        // 先导航到对话页面
        navigate(routePath.chat);
        // 再获取历史topic列表
        dispatch(fetchHistoryTopics());
    };

    const handleThemeToggle = (newTheme) => {
        // 判断旧主题是否为 light-blue，是则替换为 gradient
        const fromValue = curTheme === 'light-blue_' ? 'gradient' : curTheme;
        const toValue = newTheme === 'light-blue_' ? 'gradient' : newTheme;

        const TrackingParams = {
            eventName: 'ThemeToggle',
            eventData: {
                from: fromValue,
                to: toValue
            }
        };

        trackEvent(TrackingParams);

        dispatch(changeTheme(newTheme));
        setSettingOpen(false);
    };

    return (
        <Sider
            width={width}
            style={{
                padding: !width ? 0 : 16,
                boxShadow: !width ? 'none' : undefined,
                background: layoutMode.isMobile ? colorSurfaceCanvas : undefined
            }}
            className={`sidebar ${layoutMode.isMobile ? 'lite-sidebar' : ''}`}
            theme="light"
        >
            {/* Logo头像区域 */}
            {!layoutMode.isMobile && (
                <div className="logo-section">
                    <svg
                        role="img"
                        aria-label="logo"
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 48 48"
                        fill="none"
                    >
                        <path d="M48 0H0V48H48V0Z" fill="#E1251B" />
                        <path
                            d="M32.8421 31.8361H20.6136V11.1909H15.1562V36.8089H32.8421V31.8361Z"
                            fill="white"
                        />
                    </svg>
                </div>
            )}
            {/* 菜单按钮区域 */}
            <div className="menu-section">
                {layoutMode.isMobile ? (
                    <Button.TipButton
                        hoverTip=""
                        iconName="add-chat-20"
                        iconWidth={20}
                        iconHeight={20}
                        onClick={handleNewChat}
                        btnClassName="menu-btn new-chat-btn full-w-btn"
                        tipPlacement="right"
                        aria-label="New Chat"
                    >
                        New Chat
                    </Button.TipButton>
                ) : (
                    <Button.TipButton
                        hoverTip="New Chat"
                        iconName="add-chat-20"
                        iconWidth={20}
                        iconHeight={20}
                        onClick={handleNewChat}
                        btnClassName="menu-btn new-chat-btn"
                        tipPlacement="right"
                        tipArrow={false}
                        aria-label="New Chat"
                    />
                )}
                {layoutMode.isMobile ? (
                    <Button.TipButton
                        hoverTip=""
                        iconName=""
                        disabled={isHistoryDisabled}
                        onClick={isHistoryDisabled ? undefined : handleHistoryClick}
                        btnClassName="menu-btn history-btn full-w-btn"
                        tipPlacement="right"
                        aria-label={historyHoverTip}
                    >
                        {historyHoverTip}
                    </Button.TipButton>
                ) : (
                    <Button.TipButton
                        hoverTip={historyHoverTip}
                        iconName="history-20"
                        iconWidth={20}
                        iconHeight={20}
                        disabled={isHistoryDisabled}
                        allowDisabledTooltip
                        onClick={isHistoryDisabled ? undefined : handleHistoryClick}
                        btnClassName="menu-btn history-btn"
                        tipPlacement="right"
                        tipArrow={false}
                        aria-label={historyHoverTip}
                    />
                )}
            </div>
            {/* 底部主题切换按钮 */}
            <div className="settings-section">
                <Button.MenuButton
                    isMenuOpen={settingOpen}
                    onMenuOpenChange={(open) => setSettingOpen(open)}
                    iconName="setting-20"
                    iconWidth={20}
                    iconHeight={20}
                    btnClassName="menu-btn settings-btn"
                    menuPlacement="top"
                    menuClassName={`setting-menu ${curTheme}-setting-menu`}
                    activeMenu={settingList}
                    hoverTip="Settings"
                    aria-label="Settings"
                />
            </div>
        </Sider>
    );
};

export default Sidebar;
